import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontFamily,
  FontWeight,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type { CampaignBannerRecipeItem } from '@library/api-menu-experience'
import { TileImage as RecipeTileImage } from '../../RecipeGrid/RecipeTileImage/TileImage'

import {
  CardTextFlow,
  MerchandisingBasketButton,
  MerchandisingRecipeBannerTagStrip,
  MerchandisingRecipeSurcharge,
  type MerchandisingRecipeCardProps,
  twoLineTitleClampStyle,
} from './campaignBannerCard.atoms'

const DietaryBadge = withCitrusTheme(Box, (theme) => ({
  width: '28px',
  height: '28px',
  borderRadius: '999px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.tokens.colors.ColdGrey_700,
  color: theme.tokens.colors.White,
}))

const LabelPill = withCitrusTheme(Box, () => ({
  borderRadius: '4px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '24px',
  padding: '0 0.5rem',
  minWidth: 0,
  maxWidth: '100%',
}))

const getKeyMetadata = (subtitleTags: CampaignBannerRecipeItem['tagGroups']['subtitle']) =>
  subtitleTags.slice(0, 2)

const DietaryBadgeRow = ({ tags }: { tags: CampaignBannerRecipeItem['tagGroups']['dietary'] }) => {
  if (tags.length === 0) {
    return null
  }

  return (
    <Box
      data-testid="campaign-banner-dietary-tags"
      display={Display.Flex}
      gap={1}
      position={DisplayPosition.Absolute}
      style={{ top: 0, left: 0, zIndex: 1 }}
    >
      {tags.map((tag) => (
        <DietaryBadge key={tag.type}>
          <Text
            size={2}
            fontFamily={FontFamily.Bold}
            fontWeight={FontWeight.Bold}
            style={{ color: 'inherit', lineHeight: '1' }}
          >
            {tag.text}
          </Text>
        </DietaryBadge>
      ))}
    </Box>
  )
}

const GeneralLabelRow = ({
  labels,
}: {
  labels: CampaignBannerRecipeItem['tagGroups']['general']
}) => {
  if (labels.length === 0) {
    return null
  }

  return (
    <Box
      data-testid="campaign-banner-general-tags"
      display={Display.Flex}
      gap={1}
      position={DisplayPosition.Absolute}
      style={{
        top: 0,
        left: '1rem',
        transform: 'translateY(-50%)',
        zIndex: 1,
        minWidth: 0,
        maxWidth: 'calc(100% - 88px)',
        overflow: 'hidden',
      }}
    >
      {labels.map((label) => (
        <LabelPill
          key={`${label.type}-${label.text}`}
          style={{
            backgroundColor: label.backgroundColor.light,
            color: label.color.light,
          }}
        >
          <Text
            size={2}
            style={{
              color: 'inherit',
              lineHeight: '24px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            {label.text}
          </Text>
        </LabelPill>
      ))}
    </Box>
  )
}

const MetadataRow = ({
  subtitleTags,
}: {
  subtitleTags: CampaignBannerRecipeItem['tagGroups']['subtitle']
}) => {
  const metadata = getKeyMetadata(subtitleTags)

  if (metadata.length === 0) {
    return null
  }

  return (
    <Box
      data-testid="campaign-banner-metadata"
      display={Display.Flex}
      alignItems={AlignItems.Center}
      gap={1}
      wrap
      style={{ color: '#6D7B88' }}
    >
      {metadata.map((tag, index) => (
        <React.Fragment key={`${tag.type}-${tag.text}`}>
          {index > 0 ? (
            <Text aria-hidden="true" size={2} style={{ color: 'inherit' }}>
              |
            </Text>
          ) : null}
          <Text size={2} style={{ color: 'inherit' }}>
            {tag.text}
          </Text>
        </React.Fragment>
      ))}
    </Box>
  )
}

const onSpaceOrEnter =
  (callback: (event: React.KeyboardEvent) => void) => (event: React.KeyboardEvent) => {
    if (event.code === 'Space' || event.code === 'Enter') {
      event.preventDefault()
      callback(event)
    }
  }

const CAMPAIGN_TILE_WIDTH = 'min(82vw, 332px)'
const CAMPAIGN_TILE_HEIGHT = '352px'

export type CampaignBannerRecipeCardProps = MerchandisingRecipeCardProps<CampaignBannerRecipeItem>
export const CampaignBannerRecipeCard = ({
  recipe,
  onClick,
  onAddClick,
  onRemoveClick,
  onBlockedAddAttempt,
  canAddRecipes,
  reachedLimit,
  isInBasket,
  isOutOfStock,
  index,
}: CampaignBannerRecipeCardProps) => {
  return (
    <Box
      data-testid="campaign-banner-recipe-card"
      data-testing={isOutOfStock ? 'menuRecipeOutOfStock' : 'menuRecipeViewDetails'}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      flexShrink={0}
      onClick={() => onClick(recipe.id)}
      onKeyDown={onSpaceOrEnter(() => onClick(recipe.id))}
      role="button"
      tabIndex={0}
      style={{
        borderRadius: '4px',
        cursor: 'pointer',
        height: CAMPAIGN_TILE_HEIGHT,
        overflow: 'hidden',
        width: CAMPAIGN_TILE_WIDTH,
      }}
      boxShadow="0 8px 10px -8px rgba(19, 35, 53, 0.18)"
    >
      <Box
        position={DisplayPosition.Relative}
        style={{
          borderRadius: '4px 4px 0 0',
          height: 'min(52vw, 196px)',
          overflow: 'hidden',
        }}
      >
        <RecipeTileImage
          lazy={false}
          recipe={{
            images: [
              {
                type: 'image',
                ...recipe.image,
              },
            ],
            name: recipe.title,
          }}
          insideOverflow
          testId="campaign-banner-recipe-card-image"
        />
        <Box
          display={Display.Flex}
          position={DisplayPosition.Absolute}
          style={{
            left: 16,
            top: 8,
            zIndex: 2,
          }}
        >
          <DietaryBadgeRow tags={recipe.tagGroups.dietary} />
        </Box>
      </Box>
      <Box
        bg={Color.White}
        paddingH={3}
        paddingBottom={3}
        style={{
          flex: 1,
          marginTop: -2,
          minHeight: '124px',
          paddingTop: 0,
          position: 'relative',
        }}
      >
        <GeneralLabelRow labels={recipe.tagGroups.general} />
        <MerchandisingBasketButton
          recipeId={recipe.id}
          isInBasket={isInBasket}
          isOutOfStock={isOutOfStock}
          canAddRecipes={canAddRecipes}
          reachedLimit={reachedLimit}
          onAddClick={onAddClick}
          onRemoveClick={onRemoveClick}
          onBlockedAddAttempt={onBlockedAddAttempt}
          index={index}
        />
        <CardTextFlow>
          <Box paddingV={5} paddingBottom={2}>
            <Text
              fontFamily={FontFamily.Book}
              fontWeight={FontWeight.SemiBold}
              size={3}
              style={twoLineTitleClampStyle}
            >
              {recipe.title}
            </Text>
          </Box>
        </CardTextFlow>
        <MetadataRow subtitleTags={recipe.tagGroups.subtitle} />
        <MerchandisingRecipeSurcharge pricing={recipe.pricing} isOutOfStock={isOutOfStock} />
      </Box>
      <MerchandisingRecipeBannerTagStrip banner={recipe.tagGroups.banner} />
    </Box>
  )
}
